// The heatmap pass: the per-cell intensity field, ONE full-world quad
// (plan Part B, §B.5).
//
// The reference (`render/heatmap.ts:167-188`) fills the hex of every cell
// whose intensity is not zero, one path per bucket. `cellAt` partitions the
// plane into exactly those hexes, so the flat mode here is the same picture
// with no geometry at all: read the cell, bucket it, paint it.
//
// The transfer function is `bucketCells` / `bucketAlpha` / `bucketColor`
// transcribed: intensity 1..255 spreads evenly over `HEAT_BUCKETS`, the
// alpha ramp is linear over the mode's own span, and a mode with a dark
// endpoint mixes toward its hue with the `HEAT_DARK_EASE` exponent.
//
// `uSmooth` is the one thing the reference has no answer for: it kernel-
// interpolates the intensity over ring 1 before bucketing, which turns the
// tiles into contour steps. It is a knob, not parity.

import {
  HEAT_ALPHA_HI, HEAT_ALPHA_LO, HEAT_BUCKETS, HEAT_DARK_EASE, HEAT_HUES, HEAT_RAMPS,
} from '../../gen/presentation';
import { HEX_SIZE } from '../../gen/balance';
import { PRELUDE, SHARED_UNIFORMS, TERRAIN_RADIUS_K, type UniformSpec } from './prelude';
import { rgb } from '../../render/color';
import { worldProgram } from '../gfx/worldquad';
import type { ProgramSource } from './program';
import { heatBodyWgsl } from './wgsl/heatmap';

/** The heatmap pass's own uniforms, on top of `SHARED_UNIFORMS`. */
export const HEAT_UNIFORMS: readonly UniformSpec[] = [
  { name: 'uHeatColor', type: 'vec3', value: rgb(HEAT_HUES.sov), dynamic: true, note: 'the mode hue' },
  { name: 'uHeatDark', type: 'vec3', value: rgb(HEAT_RAMPS.sov.dark), dynamic: true, note: 'the mode dark endpoint' },
  { name: 'uHeatHasDark', type: 'int', value: 1, dynamic: true },
  { name: 'uHeatAlphaLo', type: 'float', value: HEAT_ALPHA_LO, dynamic: true },
  { name: 'uHeatAlphaHi', type: 'float', value: HEAT_ALPHA_HI, dynamic: true },
  { name: 'uHeatBuckets', type: 'int', value: HEAT_BUCKETS },
  { name: 'uHeatDarkEase', type: 'float', value: HEAT_DARK_EASE },
  { name: 'uHeatSmooth', type: 'int', value: 0 },
  { name: 'uHeatRadius', type: 'float', value: TERRAIN_RADIUS_K * HEX_SIZE, note: 'the smooth mode kernel' },
];

/** Every uniform the heatmap program declares. */
export const HEAT_ALL_UNIFORMS: readonly UniformSpec[] = [...SHARED_UNIFORMS, ...HEAT_UNIFORMS];

const BODY = `
uniform vec3  uHeatColor;
uniform vec3  uHeatDark;
uniform int   uHeatHasDark;
uniform float uHeatAlphaLo;
uniform float uHeatAlphaHi;
uniform int   uHeatBuckets;
uniform float uHeatDarkEase;
uniform int   uHeatSmooth;
uniform float uHeatRadius;

float intensityAt(ivec2 c) {
  return inGrid(c) ? texelFetch(uHeat, c, 0).r * 255.0 : 0.0;
}

void main() {
  vec2 p = vWorld;
  ivec2 c0 = cellAt(p);
  // The tint covers water and only water (R103): rock is never owned,
  // fought over or worked, so a ridge cell is dropped rather than drawn.
  if (!inGrid(c0) || kindOf(c0) == 2) { fragColor = vec4(0.0); return; }

  float v;
  if (uHeatSmooth == 1) {
    // The early-out (R3082, R493): read the ring first. When the cell and
    // its six neighbours all hold intensity 0, the weighted sum is 0, so the
    // pixel draws nothing, and the kernel taps are skipped. Only the
    // intensity reads stay; the result does not change by one bit.
    ivec2 cs[7];
    float hs[7];
    float hmax = 0.0;
    for (int i = 0; i < 7; i++) {
      cs[i] = (i == 0) ? c0 : neighbor(c0, i - 1);
      hs[i] = intensityAt(cs[i]);
      hmax = max(hmax, hs[i]);
    }
    if (hmax == 0.0) { fragColor = vec4(0.0); return; }

    float invR2 = 1.0 / (uHeatRadius * uHeatRadius);
    float num = 0.0, den = 0.0;
    for (int i = 0; i < 7; i++) {
      if (kindOf(cs[i]) == 2) continue;
      float k; vec2 dk;
      kern(p, cellCenter(cs[i]), invR2, k, dk);
      num += k * hs[i];
      den += k;
    }
    v = (den > 1e-6) ? num / den : 0.0;
  } else {
    v = intensityAt(c0);
  }

  // The server saying "nothing here" is intensity 0, and it draws nothing.
  if (v < 1.0) { fragColor = vec4(0.0); return; }

  float B = float(uHeatBuckets);
  float b = min(floor((v - 1.0) * B / 255.0), B - 1.0);
  float t = (uHeatBuckets <= 1) ? 1.0 : b / (B - 1.0);

  vec3 c = (uHeatHasDark == 1) ? mix(uHeatDark, uHeatColor, pow(t, uHeatDarkEase))
                               : uHeatColor;
  float a = uHeatAlphaLo + (uHeatAlphaHi - uHeatAlphaLo) * t;
  fragColor = vec4(c * a, a);
}
`;

/** The complete heatmap fragment shader, prelude included. */
export const HEAT_FRAG = PRELUDE + BODY;

/** The heatmap pass, as both backends read it. */
export function heatProgram(): ProgramSource {
  return worldProgram({
    name: 'heatmap', fragment: HEAT_FRAG, raw: true, uniforms: HEAT_ALL_UNIFORMS,
    // The twin writes the BODY alone. `worldProgram` puts the generated
    // header and the shared world vertex entry in front of it, from the
    // block it assembled here.
    wgslBody: heatBodyWgsl(),
  });
}
